import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { notification } from "antd";
import axios from "axios";
import { responderSubido } from "./respuestaSubido";
import { wsSoporteConectado } from "./estadoWsSoporte";

const INTERVALO_POLLING = 12000;
const INTERVALO_POLLING_LISTA = 30000;
const INTERVALO_POLLING_CONTADOR = 60000;
const MIN_GAP_REFRESCO = 8000;
const DEBOUNCE_CONTADOR = 1000;

const PAGE_SIZE = 25;
const TOPE_ACUMULADO = 100;
const DEBOUNCE_BUSQUEDA = 400;

const ENDPOINT_LISTA = {
  cliente: "soporte/misTickets",
  soporte: "soporte/bandejaActiva",
  peticiones: "soporte/peticionesSoporte",
};

const CAMPO_NO_LEIDOS = {
  cliente: "noLeidosCliente",
  soporte: "noLeidosSoporte",
  peticiones: "noLeidosRevisor",
};

const useChatSoporte = ({
  headersToken,
  chatAbierto = false,
  modoSoporte = false,
  modoPeticiones = false,
  listaActiva = true,
  habilitado = true,
}) => {
  const modo = modoSoporte ? "soporte" : modoPeticiones ? "peticiones" : "cliente";
  const campoNoLeidos = CAMPO_NO_LEIDOS[modo];

  const [tickets, setTickets] = useState([]);
  const [ticketActivo, setTicketActivo] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [loadingMensajes, setLoadingMensajes] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [creando, setCreando] = useState(false);
  const [respondiendo, setRespondiendo] = useState(false);
  const [wsConectado, setWsConectado] = useState(wsSoporteConectado());

  // Paginación y búsqueda (modo soporte).
  const [busqueda, setBusquedaTexto] = useState("");
  const [buscando, setBuscando] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [noLeidosServidor, setNoLeidosServidor] = useState(0);
  const [deltaNoLeidos, setDeltaNoLeidos] = useState(0);

  const headers = useMemo(() => ({ headers: headersToken }), [headersToken]);

  const ticketActivoRef = useRef(null);
  useEffect(() => {
    ticketActivoRef.current = ticketActivo;
  }, [ticketActivo]);

  const chatAbiertoRef = useRef(chatAbierto);
  useEffect(() => {
    chatAbiertoRef.current = chatAbierto;
  }, [chatAbierto]);

  const listaActivaRef = useRef(listaActiva);
  useEffect(() => {
    listaActivaRef.current = listaActiva;
  }, [listaActiva]);

  const busquedaRef = useRef("");
  const paginaMaxRef = useRef(1);
  const seqRef = useRef(0);
  const listaCargadaRef = useRef(false);

  const ticketsRef = useRef([]);
  useEffect(() => {
    ticketsRef.current = tickets;
  }, [tickets]);

  const ultimoFetchRef = useRef(0);
  const timerRefrescoRef = useRef(null);
  const timerContadorRef = useRef(null);

  useEffect(() => {
    const alCambiar = (e) => setWsConectado(!!e.detail?.conectado);
    window.addEventListener("soporte_ws_estado", alCambiar);
    return () => window.removeEventListener("soporte_ws_estado", alCambiar);
  }, []);

  const pedirContador = useCallback(() => {
    if (!habilitado) return;
    axios
      .post("soporte/contadorNoLeidos", { modo }, headers)
      .then((r) => {
        if (r.data.success === 2) {
          setNoLeidosServidor(r.data.noLeidosTotal || 0);
          setDeltaNoLeidos(0);
        }
      })
      .catch(() => {});
  }, [headers, modo, habilitado]);

  const cargarContador = useCallback(() => {
    if (timerContadorRef.current) clearTimeout(timerContadorRef.current);
    timerContadorRef.current = setTimeout(() => {
      timerContadorRef.current = null;
      pedirContador();
    }, DEBOUNCE_CONTADOR);
  }, [pedirContador]);

  const pedirTickets = useCallback(
    ({ silencioso } = {}) => {
      if (!habilitado) return;
      if (!silencioso) setLoadingTickets(true);
      ultimoFetchRef.current = Date.now();

      // Se re-piden de golpe todas las páginas ya cargadas, así el refresco en
      // segundo plano no colapsa la lista a la primera página.
      const cuerpo =
        modo === "soporte"
          ? {
              page: 1,
              pageSize: Math.min(PAGE_SIZE * paginaMaxRef.current, TOPE_ACUMULADO),
              busqueda: busquedaRef.current,
            }
          : {};

      const seq = ++seqRef.current;

      axios
        .post(ENDPOINT_LISTA[modo], cuerpo, headers)
        .then((r) => {
          if (seq !== seqRef.current) return;
          if (r.data.success === 2) {
            listaCargadaRef.current = true;
            setTickets(r.data.tickets || []);
            setNoLeidosServidor(r.data.noLeidosTotal || 0);
            setDeltaNoLeidos(0);
            if (modo === "soporte") {
              setTotalRecords(r.data.totalRecords || 0);
              setBuscando(!!r.data.buscando);
            }
          }
        })
        .finally(() => {
          if (!silencioso) setLoadingTickets(false);
        });
    },
    [headers, modo, habilitado],
  );

  const cargarTickets = useCallback(
    ({ silencioso } = {}) => {
      if (!silencioso) {
        if (timerRefrescoRef.current) {
          clearTimeout(timerRefrescoRef.current);
          timerRefrescoRef.current = null;
        }
        pedirTickets({});
        return;
      }

      if (timerRefrescoRef.current) return;
      const falta = Math.max(0, MIN_GAP_REFRESCO - (Date.now() - ultimoFetchRef.current));
      timerRefrescoRef.current = setTimeout(() => {
        timerRefrescoRef.current = null;
        pedirTickets({ silencioso: true });
      }, falta);
    },
    [pedirTickets],
  );

  const refrescarFondo = useCallback(() => {
    if (listaActivaRef.current) cargarTickets({ silencioso: true });
    else cargarContador();
  }, [cargarTickets, cargarContador]);

  useEffect(
    () => () => {
      if (timerRefrescoRef.current) clearTimeout(timerRefrescoRef.current);
      if (timerContadorRef.current) clearTimeout(timerContadorRef.current);
    },
    [],
  );

  const cargarMas = useCallback(() => {
    if (modo !== "soporte" || cargandoMas) return;
    const siguiente = paginaMaxRef.current + 1;

    setCargandoMas(true);
    axios
      .post(
        "soporte/bandejaActiva",
        { page: siguiente, pageSize: PAGE_SIZE, busqueda: busquedaRef.current },
        headers,
      )
      .then((r) => {
        if (r.data.success === 2) {
          const nuevos = r.data.tickets || [];
          if (nuevos.length > 0) {
            paginaMaxRef.current = siguiente;
            setTickets((prev) => {
              const porId = new Map(prev.map((t) => [String(t._id), t]));
              for (const t of nuevos) porId.set(String(t._id), t);
              return [...porId.values()];
            });
          }
          setTotalRecords(r.data.totalRecords || 0);
        }
      })
      .finally(() => setCargandoMas(false));
  }, [headers, modo, cargandoMas]);

  const debounceRef = useRef(null);
  const setBusqueda = useCallback(
    (texto) => {
      setBusquedaTexto(texto);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        busquedaRef.current = texto;
        paginaMaxRef.current = 1;
        cargarTickets();
      }, DEBOUNCE_BUSQUEDA);
    },
    [cargarTickets],
  );

  useEffect(() => () => debounceRef.current && clearTimeout(debounceRef.current), []);

  const cargarMensajes = useCallback(
    (ticketId, { silencioso } = {}) => {
      if (!silencioso) setLoadingMensajes(true);
      axios
        .post("soporte/obtenerMensajes", { ticketId }, headers)
        .then((r) => {
          const abierto = ticketActivoRef.current;
          if (!abierto || String(abierto._id) !== String(ticketId)) return;
          if (r.data.success === 2) {
            setTicketActivo(r.data.ticket);
            setMensajes(r.data.mensajes || []);
          } else if (!silencioso) {
            notification.info({ message: r.data.message || "Este reporte no se abrió, intenta de nuevo." });
          }
        })
        .finally(() => {
          if (!silencioso) setLoadingMensajes(false);
        });
    },
    [headers],
  );

  const bajarNoLeidos = useCallback(
    (ticketId) => {
      const actual = ticketsRef.current.find((t) => String(t._id) === String(ticketId));
      const cuantos = actual?.[campoNoLeidos] || 0;
      if (!cuantos) return;

      setTickets((prev) =>
        prev.map((t) => (String(t._id) === String(ticketId) ? { ...t, [campoNoLeidos]: 0 } : t)),
      );
      setNoLeidosServidor((n) => Math.max(0, n - cuantos));
    },
    [campoNoLeidos],
  );

  const seleccionarTicket = useCallback(
    (ticket) => {
      ticketActivoRef.current = ticket;
      setTicketActivo(ticket);
      bajarNoLeidos(ticket._id);
      cargarMensajes(ticket._id);
    },
    [cargarMensajes, bajarNoLeidos],
  );

  const volverALista = useCallback(() => {
    ticketActivoRef.current = null;
    setTicketActivo(null);
    setMensajes([]);
  }, []);

  const enviarMensaje = useCallback(
    (texto) => {
      const ticket = ticketActivoRef.current;
      if (!ticket || !texto?.trim()) return;

      setEnviando(true);
      axios
        .post("soporte/enviarMensaje", { ticketId: ticket._id, texto }, headers)
        .then((r) => {
          if (r.data.success === 2) {
            cargarMensajes(ticket._id, { silencioso: true });
            setTickets((prev) =>
              prev.map((t) =>
                String(t._id) === String(ticket._id)
                  ? {
                      ...t,
                      ultimoMensajeTexto: texto,
                      ultimoMensajeFecha: new Date(),
                      ultimoMensajeEsAgente: modo === "soporte",
                      ultimoMensajeEsSistema: false,
                      ultimoMensajeUsuarioId: undefined,
                    }
                  : t,
              ),
            );
            cargarTickets({ silencioso: true });
          } else {
            notification.info({ message: r.data.message || "Tu mensaje no se envió, intenta de nuevo." });
          }
        })
        .catch(() => notification.info({ message: "Revisa tu conexión e intenta enviar de nuevo." }))
        .finally(() => setEnviando(false));
    },
    [headers, cargarMensajes, cargarTickets, modo],
  );

  const responderSubidoTicket = useCallback(
    (ticketId, respuesta) => {
      setRespondiendo(true);
      return responderSubido(ticketId, respuesta, headersToken)
        .then(({ ok, message }) => {
          if (ok) {
            cargarMensajes(ticketId, { silencioso: true });
            cargarTickets({ silencioso: true });
          } else {
            notification.info({ message: message || "Tu respuesta no se guardó, intenta de nuevo." });
          }
          return ok;
        })
        .finally(() => setRespondiendo(false));
    },
    [headersToken, cargarMensajes, cargarTickets],
  );

  // formData ya viene armado con texto, urlVista e imagen1..imagen3 (ModalNuevoTicket).
  const crearPreReporte = useCallback(
    (formData, onExito) => {
      setCreando(true);
      const headersMulti = { ...headersToken };
      delete headersMulti["Content-Type"];
      axios
        .post("soporte/crearPreReporte", formData, { headers: headersMulti })
        .then((r) => {
          if (r.data.success === 2) {
            cargarTickets();
            seleccionarTicket(r.data.ticket);
            onExito?.();
          } else {
            notification.info({ message: r.data.message || "Tu reporte no se guardó, intenta de nuevo." });
          }
        })
        .catch(() => notification.info({ message: "Revisa tu conexión e intenta de nuevo." }))
        .finally(() => setCreando(false));
    },
    [headersToken, cargarTickets, seleccionarTicket],
  );

  useEffect(() => {
    if (!habilitado) return;
    if (!listaActiva) {
      if (!listaCargadaRef.current) pedirContador();
      return;
    }
    if (listaCargadaRef.current) cargarTickets({ silencioso: true });
    else cargarTickets();
  }, [listaActiva, habilitado, cargarTickets, pedirContador]);

  useEffect(() => {
    const handleSoporteEvent = (e) => {
      const data = e.detail || {};
      const activo = ticketActivoRef.current;
      const esElAbierto = !!activo && String(data.ticketId) === String(activo._id);
      const leyendoAhora = esElAbierto && chatAbiertoRef.current && document.visibilityState === "visible";

      if (modo === "soporte" && data.tipo === "mensaje" && !leyendoAhora) {
        setTickets((prev) =>
          prev.map((t) =>
            String(t._id) === String(data.ticketId)
              ? {
                  ...t,
                  ultimoMensajeTexto: data.texto ?? t.ultimoMensajeTexto,
                  ultimoMensajeFecha: new Date(),
                  ultimoMensajeEsAgente: !!data.esAgente,
                  ultimoMensajeEsSistema: false,
                  ultimoMensajeUsuarioId: undefined,
                  noLeidosSoporte: data.esAgente ? 0 : (t.noLeidosSoporte || 0) + 1,
                }
              : t,
          ),
        );
        if (!data.esAgente) setDeltaNoLeidos((d) => d + 1);
      }

      refrescarFondo();
      if (leyendoAhora) cargarMensajes(activo._id, { silencioso: true });
    };
    window.addEventListener("soporte_event", handleSoporteEvent);
    return () => window.removeEventListener("soporte_event", handleSoporteEvent);
  }, [refrescarFondo, cargarMensajes, modo]);

  const hayTicketAbierto = !!ticketActivo;

  const refrescar = useCallback(() => {
    if (document.visibilityState !== "visible") return;
    refrescarFondo();
    const activo = ticketActivoRef.current;
    if (chatAbiertoRef.current && activo) cargarMensajes(activo._id, { silencioso: true });
  }, [refrescarFondo, cargarMensajes]);

  useEffect(() => {
    if (!habilitado || wsConectado) return undefined;
    const ritmo = !listaActiva
      ? INTERVALO_POLLING_CONTADOR
      : chatAbierto && hayTicketAbierto
        ? INTERVALO_POLLING
        : INTERVALO_POLLING_LISTA;
    const intervalo = setInterval(refrescar, ritmo);
    return () => clearInterval(intervalo);
  }, [habilitado, wsConectado, listaActiva, chatAbierto, hayTicketAbierto, refrescar]);

  const wsPrevioRef = useRef(wsConectado);
  const huboDesconexionRef = useRef(false);
  useEffect(() => {
    if (!wsConectado && wsPrevioRef.current) huboDesconexionRef.current = true;
    const reconecto = wsConectado && !wsPrevioRef.current && huboDesconexionRef.current;
    wsPrevioRef.current = wsConectado;
    if (reconecto) refrescar();
  }, [wsConectado, refrescar]);

  useEffect(() => {
    document.addEventListener("visibilitychange", refrescar);
    return () => document.removeEventListener("visibilitychange", refrescar);
  }, [refrescar]);

  useEffect(() => {
    if (!chatAbierto) return;
    const activo = ticketActivoRef.current;
    if (activo) cargarMensajes(activo._id, { silencioso: true });
  }, [chatAbierto, cargarMensajes]);

  const noLeidos = Math.max(0, noLeidosServidor + deltaNoLeidos);

  const hayMas = modo === "soporte" && tickets.length < totalRecords && tickets.length < TOPE_ACUMULADO;

  return {
    tickets,
    ticketActivo,
    mensajes,
    noLeidos,
    loadingTickets,
    loadingMensajes,
    enviando,
    creando,
    respondiendo,
    cargarTickets,
    seleccionarTicket,
    volverALista,
    enviarMensaje,
    crearPreReporte,
    responderSubido: responderSubidoTicket,
    // Paginación y búsqueda: solo tienen sentido en modo soporte.
    busqueda,
    setBusqueda,
    buscando,
    totalRecords,
    hayMas,
    cargandoMas,
    cargarMas,
  };
};

export default useChatSoporte;
